// client/src/pages/Weddings/steps/DessertsStep/components/DessertCard.jsx
import React, { useMemo } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Stack,
  Divider,
  Button,
} from "@mui/material";
import QuantityStepper from "./QuantityStepper";
import { snapQtyPieces } from "../utils/math";
import { useWeddingWizard } from "../../../../../context/WeddingWizardContext";
import { SUBCATEGORY_META } from "../utils/constants";

export default function DessertCard({ it, selections }) {
  const { upsertDessert } = useWeddingWizard();

  const minSet = Number(it?.wedding?.minSet) || 1;
  const stepPieces = Number(it?.wedding?.itemsPerSet) || 12;
  const minPieces = minSet * stepPieces;
  const unit = Number(it?.wedding?.price) || 0;

  const current = useMemo(
    () => selections.find((s) => s.patisserieId === it._id),
    [selections, it._id]
  );

  const qty = Number(current?.quantity) || 0;
  const lineTotal = unit * qty;

  // ⬇️ Snapshot everything we need for Review/Emails/Order records
  const extras = {
    displayNameSnapshot: it.displayName,
    itemsPerSet: stepPieces,
    unitPriceSnapshot: unit,
    subcategoryKeySnapshot: it.subcategory || "other",
    subcategoryLabelSnapshot:
      SUBCATEGORY_META[it.subcategory]?.label || it.subcategory || "Other",
  };

  const handleChange = (raw) => {
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return;
    if (n === 0) {
      upsertDessert({ patisserieId: it._id, quantity: 0, ...extras });
    } else {
      const next = snapQtyPieces(n, minPieces, stepPieces);
      upsertDessert({ patisserieId: it._id, quantity: next, ...extras });
    }
  };

  const inc = () => {
    const next = qty === 0 ? minPieces : qty + stepPieces;
    upsertDessert({ patisserieId: it._id, quantity: next, ...extras });
  };

  const dec = () => {
    if (qty === 0) return;
    const next = qty <= minPieces ? 0 : Math.max(0, qty - stepPieces);
    upsertDessert({ patisserieId: it._id, quantity: next, ...extras });
  };

  const clear = () =>
    upsertDessert({ patisserieId: it._id, quantity: 0, ...extras });

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}>
      {it.image && (
        <CardMedia
          component="img"
          image={it.image}
          alt={it.displayName}
          sx={{
            width: "100%",
            height: { xs: 300, sm: 160, md: 280 },
            objectFit: "cover",
            objectPosition: "center",
            backgroundColor: (t) => t.palette.action.hover,
          }}
        />
      )}

      <CardContent
        sx={{
          p: { xs: 1.5, sm: 1.75, md: 2 },
          display: "flex",
          flexDirection: "column",
          gap: { xs: 0.5, md: 0.75 },
        }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {it.displayName}
        </Typography>

        <Stack direction="row" spacing={0.75} alignItems="baseline">
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            ${unit.toFixed(2)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            each
          </Typography>
        </Stack>

        <QuantityStepper
          value={qty}
          onChange={handleChange}
          onInc={inc}
          onDec={dec}
          minPieces={minPieces}
          stepPieces={stepPieces}
        />

        {qty > 0 && (
          <>
            <Divider sx={{ my: { xs: 0.75, md: 1 } }} />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Line subtotal
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                ${lineTotal.toFixed(2)}
              </Typography>
            </Stack>
            <Button
              onClick={clear}
              color="inherit"
              size="small"
              sx={{ alignSelf: "flex-end", textTransform: "none", mt: 0.25 }}>
              Clear
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
